import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';
import { ArrowLeft, Save, Plus, Trash2, Upload } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminServices = () => {
  const navigate = useNavigate();
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editingId, setEditingId] = useState(null);

  useEffect(() => {
    fetchServices();
  }, []);

  const fetchServices = async () => {
    try {
      const response = await axios.get(`${API}/services`);
      setServices(response.data);
    } catch (error) {
      console.error('Error fetching services:', error);
    }
  };

  const handleImageUpload = async (e, serviceId) => {
    const file = e.target.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await axios.post(`${API}/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      const imageUrl = `${BACKEND_URL}${response.data.url}`;
      
      // Update service with new image
      const service = services.find(s => s.id === serviceId);
      if (service) {
        service.image = imageUrl;
        await handleUpdate(serviceId, { image: imageUrl });
      }
      
      toast.success('Image uploaded successfully!');
    } catch (error) {
      toast.error('Failed to upload image');
      console.error(error);
    }
  };

  const handleUpdate = async (id, updates) => {
    try {
      await axios.put(`${API}/services/${id}`, updates);
      fetchServices();
      toast.success('Service updated!');
    } catch (error) {
      toast.error('Failed to update service');
      console.error(error);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this service?')) return;
    
    try {
      await axios.delete(`${API}/services/${id}`);
      fetchServices();
      toast.success('Service deleted!');
    } catch (error) {
      toast.error('Failed to delete service');
      console.error(error);
    }
  };

  const handleFieldChange = (id, field, value) => {
    setServices(services.map(s => 
      s.id === id ? { ...s, [field]: value } : s
    ));
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white shadow-sm border-b">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <Button variant="ghost" onClick={() => navigate('/admin/dashboard')}>
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to Dashboard
              </Button>
              <h1 className="text-2xl font-bold text-gray-900">Manage Services</h1>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="space-y-6">
          {services.map((service) => (
            <Card key={service.id}>
              <CardContent className="p-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Image Section */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Service Image
                    </label>
                    <div className="relative">
                      <img
                        src={service.image}
                        alt={service.name}
                        className="w-full h-64 object-cover rounded-lg"
                      />
                      <label className="absolute bottom-2 right-2 cursor-pointer">
                        <div className="bg-amber-800 hover:bg-amber-900 text-white px-4 py-2 rounded-lg flex items-center space-x-2">
                          <Upload className="w-4 h-4" />
                          <span>Change Image</span>
                        </div>
                        <input
                          type="file"
                          accept="image/*"
                          className="hidden"
                          onChange={(e) => handleImageUpload(e, service.id)}
                        />
                      </label>
                    </div>
                  </div>

                  {/* Details Section */}
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Service Name
                      </label>
                      <Input
                        value={service.name}
                        onChange={(e) => handleFieldChange(service.id, 'name', e.target.value)}
                        onBlur={() => handleUpdate(service.id, { name: service.name })}
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Description
                      </label>
                      <Textarea
                        value={service.description}
                        onChange={(e) => handleFieldChange(service.id, 'description', e.target.value)}
                        onBlur={() => handleUpdate(service.id, { description: service.description })}
                        rows={3}
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Duration
                      </label>
                      <Input
                        value={service.duration}
                        onChange={(e) => handleFieldChange(service.id, 'duration', e.target.value)}
                        onBlur={() => handleUpdate(service.id, { duration: service.duration })}
                        placeholder="e.g., 4-6 hours"
                      />
                    </div>

                    <div className="flex items-center space-x-4">
                      <label className="flex items-center space-x-2">
                        <input
                          type="checkbox"
                          checked={service.popular}
                          onChange={(e) => {
                            const newValue = e.target.checked;
                            handleFieldChange(service.id, 'popular', newValue);
                            handleUpdate(service.id, { popular: newValue });
                          }}
                          className="rounded"
                        />
                        <span className="text-sm font-medium text-gray-700">Mark as Popular</span>
                      </label>

                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleDelete(service.id)}
                        className="border-red-500 text-red-500 hover:bg-red-50"
                      >
                        <Trash2 className="w-4 h-4 mr-1" />
                        Delete
                      </Button>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        <Card className="mt-8 bg-amber-50 border-amber-200">
          <CardContent className="p-6">
            <p className="text-gray-700">
              💡 <strong>Tip:</strong> Click on any field to edit. Changes are saved automatically when you click outside the field. 
              Upload new images by clicking "Change Image" on any service card.
            </p>
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

export default AdminServices;
